import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl, API_CONFIG } from '../config/api';
import {
  BellIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

interface Alert {
  id: number;
  title: string;
  message: string;
  severity: string;
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

const Alerts: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.ALERTS));
      
      // Debug log to see the response structure
      console.log('Alerts API Response:', response.data);
      
      // Backend returns { success: true, data: alerts[] }
      if (response.data.success && response.data.data) {
        setAlerts(response.data.data);
      } else {
        setAlerts([]);
      }
    } catch (error: any) {
      console.error('Fetch alerts error:', error);
      setError(error.response?.data?.error || error.message || 'Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  const markAlertRead = async (alertId: number) => {
    try {
      const response = await axios.put(buildApiUrl(`${API_CONFIG.ENDPOINTS.ALERTS_MARK_READ}/${alertId}/read`));
      console.log('Mark alert read response:', response.data);
      
      if (response.data.success) {
        fetchAlerts(); // Refresh data
      } else {
        setError(response.data.error || 'Failed to mark alert as read');
      }
    } catch (error: any) {
      console.error('Mark alert read error:', error);
      setError(error.response?.data?.error || error.message || 'Failed to mark alert as read');
    }
  };

  const testEmailNotification = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(buildApiUrl(API_CONFIG.ENDPOINTS.ALERTS_TEST_EMAIL));
      console.log('Test email response:', response.data);
      
      if (response.data.success) {
        alert('Test email sent successfully!');
        fetchAlerts(); // Refresh data to show the test alert
      } else {
        setError(response.data.error || 'Failed to send test email');
      }
    } catch (error: any) {
      console.error('Test email error:', error);
      setError(error.response?.data?.error || error.message || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'badge-danger';
      case 'high':
        return 'badge-danger';
      case 'medium':
        return 'badge-warning';
      case 'low':
        return 'badge-info';
      default:
        return 'badge-info';
    }
  };


  const filteredAlerts = alerts.filter(alert => {
    if (filterSeverity !== 'all' && alert.severity !== filterSeverity) return false;
    if (filterStatus !== 'all') {
      if (filterStatus === 'read' && !alert.is_read) return false;
      if (filterStatus === 'unread' && alert.is_read) return false;
    }
    return true;
  });

  const alertStats = {
    total: alerts.length,
    read: alerts.filter(a => a.is_read).length,
    unread: alerts.filter(a => !a.is_read).length,
    critical: alerts.filter(a => a.severity === 'critical').length,
    error: alerts.filter(a => a.severity === 'error').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    info: alerts.filter(a => a.severity === 'info').length,
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alerts Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage system alerts and notifications
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={testEmailNotification}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Testing...' : 'Test Email'}
          </button>
          <button
            onClick={fetchAlerts}
            disabled={loading}
            className="btn-secondary"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setError(null)}
                  className="bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Statistics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BellIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Alerts</p>
                <p className="text-2xl font-semibold text-gray-900">{alertStats.total}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Read</p>
                <p className="text-2xl font-semibold text-gray-900">{alertStats.read}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Unread</p>
                <p className="text-2xl font-semibold text-gray-900">{alertStats.unread}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-6 w-6 text-danger-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Critical</p>
                <p className="text-2xl font-semibold text-gray-900">{alertStats.critical}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center">
            <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="form-label">Severity</label>
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value)}
                className="form-input"
              >
                <option value="all">All Severities</option>
                <option value="critical">Critical</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>

            <div>
              <label className="form-label">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="form-input"
              >
                <option value="all">All Status</option>
                <option value="read">Read</option>
                <option value="unread">Unread</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Alerts ({filteredAlerts.length} of {alerts.length})
          </h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="loading-spinner"></div>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center py-12">
              <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No alerts</h3>
              <p className="mt-1 text-sm text-gray-500">
                {alerts.length === 0 
                  ? "No alerts have been generated yet." 
                  : "No alerts match your current filters."}
              </p>
              {alerts.length === 0 && (
                <div className="mt-6">
                  <button
                    onClick={testEmailNotification}
                    className="btn-primary"
                  >
                    Send Test Alert
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Title</th>
                    <th className="table-header-cell">Message</th>
                    <th className="table-header-cell">Severity</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {filteredAlerts.map((alert) => (
                    <tr key={alert.id} className="table-row">
                      <td className="table-cell">
                        <div className="font-medium text-gray-900">{alert.title}</div>
                      </td>
                      <td className="table-cell">
                        <div className="max-w-xs truncate" title={alert.message}>
                          {alert.message}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${getSeverityColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          {alert.is_read ? (
                            <>
                              <CheckCircleIcon className="h-4 w-4 text-success-500 mr-1" />
                              <span className="text-sm text-success-600">Read</span>
                            </>
                          ) : (
                            <>
                              <ExclamationTriangleIcon className="h-4 w-4 text-warning-500 mr-1" />
                              <span className="text-sm text-warning-600">Unread</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-500">
                          {new Date(alert.created_at).toLocaleString()}
                        </div>
                        {alert.updated_at && alert.updated_at !== alert.created_at && (
                          <div className="text-xs text-gray-400">
                            Updated: {new Date(alert.updated_at).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="flex space-x-2">
                          {!alert.is_read && (
                            <button
                              onClick={() => markAlertRead(alert.id)}
                              className="btn-success text-xs"
                              title="Mark as Read"
                            >
                              <CheckCircleIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            className="btn-secondary text-xs"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && filteredAlerts.length === 0 && (
            <div className="text-center py-8">
              <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No alerts found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No alerts match the current filters.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Alerts;
